import { type ReactNode } from 'react';

export type BillFrequency = 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
export type BillStatus = 'PENDING' | 'PAID' | 'OVERDUE' | 'PROCESSING' | 'FAILED';
export type BillSource = 'MANUAL' | 'AUTO_DISCOVERED' | 'CONNECTED_ACCOUNT';
export type PaymentAccountType = 'CHECKING' | 'SAVINGS' | 'CREDIT_CARD' | 'INVESTMENT';

export interface PaymentAccount {
  id: string;
  name: string;
  type: PaymentAccountType;
  lastFour: string;
  balance: number;
  isPrimary: boolean;
  institution: string;
  logoUrl?: string;
}

export interface BillPayment {
  id: string;
  billId: string;
  amount: number;
  paymentDate: string;
  status: 'PENDING' | 'COMPLETED' | 'FAILED';
  accountId: string;
  reference: string;
  processedAt?: string;
  failureReason?: string;
}

export interface Bill {
  id: string;
  name: string;
  amount: number;
  originalAmount?: number;
  dueDate: string;
  frequency: BillFrequency;
  category: string;
  status: BillStatus;
  autoPay: boolean;
  source: BillSource;
  payeeId?: string;
  payeeLogo?: string;
  accountId?: string;
  paymentAccountId?: string;
  paymentMethod?: 'AUTO' | 'MANUAL';
  paymentInstructions?: string;
  isVariableAmount: boolean;
  averageAmount?: number;
  minAmount?: number;
  maxAmount?: number;
  confidenceScore?: number;
  lastPaidDate?: string;
  lastPaidAmount?: number;
  nextPaymentDate?: string;
  createdAt: string;
  updatedAt: string;
  metadata?: {
    [key: string]: any;
  };
  anomalyDetection?: {
    amountChange?: {
      percentage: number;
      previousAmount: number;
      isAnomaly: boolean;
    };
    dateChange?: {
      daysDiff: number;
      previousDate: string;
      isAnomaly: boolean;
    };
  };
}

export interface BillFormData extends Omit<Bill, 'id' | 'createdAt' | 'updatedAt' | 'status' | 'source' | 'metadata' | 'anomalyDetection'> {
  paymentAccountId?: string;
}

export interface BillFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (bill: BillFormData) => Promise<void>;
  bill?: Bill | null;
  onDelete?: (id: string) => void;
  isLoading?: boolean;
  paymentAccounts?: PaymentAccount[];
}

export interface BillDiscoveryResult {
  bill: BillFormData;
  confidence: number;
  source: 'TRANSACTION_HISTORY' | 'EMAIL' | 'RECEIPT' | 'MANUAL';
  matchedTransactions?: Array<{
    id: string;
    date: string;
    amount: number;
    description: string;
    accountId: string;
  }>;
  isNewVendor?: boolean;
  suggestedCategory?: string;
  suggestedFrequency?: BillFrequency;
}

export interface BillDiscoveryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddBills: (bills: BillFormData[]) => void;
  results: BillDiscoveryResult[];
  isLoading?: boolean;
  onRescan?: () => void;
  onConnectAccounts?: () => void;
}

export interface BillsListProps {
  bills: Bill[];
  onEditBill: (bill: Bill) => void;
  onDeleteBill: (id: string) => void;
  onPayBill: (bill: Bill) => void;
  onToggleAutoPay: (id: string, autoPay: boolean) => void;
  onViewBill: (bill: Bill) => void;
  filter?: 'all' | 'upcoming' | 'overdue' | 'paid' | 'autoPay' | 'manual';
  isLoading?: boolean;
  paymentAccounts?: PaymentAccount[];
  onAccountSelect?: (billId: string, accountId: string) => void;
}

export interface BillCalendarProps {
  bills: Bill[];
  selectedDate: Date | null;
  onDateSelect: (date: Date) => void;
  onBillClick: (bill: Bill) => void;
  onAddBill: (date: Date) => void;
  className?: string;
  onMonthChange?: (date: Date) => void;
}

export interface BillStats {
  totalBills: number;
  totalAmount: number;
  overdueCount: number;
  upcomingCount: number;
  autoPayCount: number;
  manualPayCount: number;
  averageMonthlyExpenditure: number;
  upcomingAmount: number;
  overdueAmount: number;
  byCategory: Array<{
    category: string;
    count: number;
    amount: number;
    percentage: number;
  }>;
}

export interface BillsContextType {
  // State
  bills: Bill[];
  paymentAccounts: PaymentAccount[];
  loading: boolean;
  processing: boolean;
  isFirstTimeUser: boolean;
  discoveryInProgress: boolean;
  
  // Core Operations
  addBill: (bill: BillFormData) => Promise<Bill>;
  updateBill: (id: string, updates: Partial<Bill>) => Promise<Bill | undefined>;
  deleteBill: (id: string) => Promise<void>;
  
  // Payment Operations
  processBillPayment: (billId: string, options?: { amount?: number; accountId?: string }) => Promise<Bill>;
  toggleAutoPay: (id: string, autoPay: boolean, accountId?: string) => Promise<Bill>;
  
  // Discovery & Analysis
  discoverBills: (options?: { forceRescan: boolean }) => Promise<BillDiscoveryResult[]>;
  analyzeSpending: (options?: { startDate?: string; endDate?: string }) => Promise<{
    monthlyAverage: number;
    byCategory: Array<{ category: string; amount: number }>;
    upcomingBills: Array<{ date: string; amount: number }>;
  }>;
  
  // Data Retrieval
  getUpcomingBills: (options?: { daysAhead?: number }) => Bill[];
  getOverdueBills: () => Bill[];
  getPaidBills: (options?: { startDate?: string; endDate?: string }) => Bill[];
  getBillStats: () => BillStats;
  getPaymentHistory: (billId: string) => Promise<BillPayment[]>;
  
  // Account Management
  refreshAccounts: () => Promise<void>;
  setPrimaryAccount: (accountId: string) => Promise<void>;
  
  // Anomaly Detection
  checkForAnomalies: (billId: string) => Promise<{
    amountAnomaly: boolean;
    dateAnomaly: boolean;
    details: any;
  }>;
  
  // Optimization
  optimizePayments: (options?: {
    targetAccountId?: string;
    targetDate?: string;
  }) => Promise<Array<{
    billId: string;
    suggestedPaymentDate: string;
    suggestedAmount: number;
    savings: number;
    reason: string;
  }>>;
}

export interface BillsProviderProps {
  children: ReactNode;
  userId: string;
}

// Mock data for development
export const mockPaymentAccounts: PaymentAccount[] = [
  {
    id: 'acct_123',
    name: 'Primary Checking',
    type: 'CHECKING',
    lastFour: '4582',
    balance: 12500.42,
    isPrimary: true,
    institution: 'Chase Bank',
    logoUrl: 'https://logo.clearbit.com/chase.com',
  },
  {
    id: 'acct_456',
    name: 'Savings',
    type: 'SAVINGS',
    lastFour: '9012',
    balance: 35000.00,
    isPrimary: false,
    institution: 'Chase Bank',
    logoUrl: 'https://logo.clearbit.com/chase.com',
  },
  {
    id: 'acct_789',
    name: 'Platinum Card',
    type: 'CREDIT_CARD',
    lastFour: '3456',
    balance: -1250.75,
    isPrimary: false,
    institution: 'American Express',
    logoUrl: 'https://logo.clearbit.com/americanexpress.com',
  },
];

export const mockBills: Bill[] = [
  {
    id: '1',
    name: 'Pacific Gas & Electric',
    amount: 125.75,
    originalAmount: 125.75,
    dueDate: new Date().toISOString().split('T')[0],
    frequency: 'MONTHLY',
    category: 'Utilities',
    status: 'PENDING',
    autoPay: true,
    source: 'AUTO_DISCOVERED',
    payeeLogo: 'https://logo.clearbit.com/pge.com',
    paymentAccountId: 'acct_123',
    paymentMethod: 'AUTO',
    isVariableAmount: true,
    averageAmount: 118.42,
    minAmount: 89.50,
    maxAmount: 156.30,
    confidenceScore: 0.98,
    lastPaidDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    lastPaidAmount: 118.42,
    nextPaymentDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    createdAt: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date().toISOString(),
    anomalyDetection: {
      amountChange: {
        percentage: 6.2,
        previousAmount: 118.42,
        isAnomaly: false,
      },
    },
  },
  {
    id: '2',
    name: 'Netflix',
    amount: 15.99,
    dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    frequency: 'MONTHLY',
    category: 'Entertainment',
    status: 'PENDING',
    autoPay: true,
    source: 'CONNECTED_ACCOUNT',
    payeeLogo: 'https://logo.clearbit.com/netflix.com',
    paymentAccountId: 'acct_789',
    paymentMethod: 'AUTO',
    isVariableAmount: false,
    confidenceScore: 1.0,
    lastPaidDate: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    lastPaidAmount: 15.99,
    nextPaymentDate: new Date(Date.now() + 35 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    createdAt: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: '3',
    name: 'Rent',
    amount: 1850.00,
    dueDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    frequency: 'MONTHLY',
    category: 'Housing',
    status: 'OVERDUE',
    autoPay: false,
    source: 'MANUAL',
    paymentMethod: 'MANUAL',
    isVariableAmount: false,
    confidenceScore: 1.0,
    lastPaidDate: new Date(Date.now() - 32 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    lastPaidAmount: 1850.00,
    nextPaymentDate: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    createdAt: new Date(Date.now() - 400 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date().toISOString(),
    metadata: {
      landlord: {
        name: 'John Smith',
        phone: '(555) 123-4567',
        email: 'john.smith@example.com',
      },
      leaseEnd: '2024-12-31',
    },
  },
];

export const mockBillDiscoveryResults: BillDiscoveryResult[] = [
  {
    bill: {
      name: 'Spotify Premium',
      amount: 12.99,
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      frequency: 'MONTHLY',
      category: 'Entertainment',
      autoPay: true,
      paymentAccountId: 'acct_789',
      isVariableAmount: false,
    },
    confidence: 0.97,
    source: 'TRANSACTION_HISTORY',
    matchedTransactions: [
      {
        id: 'txn_spotify_1',
        date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        amount: 12.99,
        description: 'SPOTIFY PREMIUM',
        accountId: 'acct_789',
      },
      {
        id: 'txn_spotify_2',
        date: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
        amount: 12.99,
        description: 'SPOTIFY PREMIUM',
        accountId: 'acct_789',
      },
    ],
    isNewVendor: false,
    suggestedCategory: 'Entertainment',
    suggestedFrequency: 'MONTHLY',
  },
  // Add more mock discoveries as needed
];
